import { Box } from '@material-ui/core';
import { PaymentMethodType } from '@nemuru-eng/js-api-client';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { NoConditionsAvailableException } from '@/components/modules/Application/components/PaymentMethod/_shared/exceptions/NoConditionsAvailableException';
import { useCheckoutSessionContext } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/_shared/contexts/CheckoutSessionContext';
import { NextActions } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/_shared/components/NextActions/NextActions';
import { PaymentIntentList } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/_shared/components/PaymentIntentList/PaymentIntentList';
import { NoConditionsAvailable } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/_shared/components/ConditionsAndProgress/Conditions/NoConditionsAvailable';
import { LoanPaymentMethodDetailEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/_shared/entities/PaymentMethodDetail/LoanPaymentMethodDetailEntity';
import { useLoanPaymentIntent } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/_shared/hooks/useLoanPaymentIntent';
import { ConditionsAndProgress } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/ConditionsAndProgress/ConditionsAndProgress';
import {
  NextActionFactory,
  NextActionFactoryProps,
} from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/NextActions/components/NextActionFactory';
import { MerchantPosDialog } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/PaymentIntent/Multilender/MerchantPos/MerchantPosDialog';
import { CustomSkeleton } from '@/components/shared/components/CustomSkeleton';

interface LoanPaymentMethodProps {
  paymentMethodType: PaymentMethodType;
}

export const LoanPaymentMethod = ({ paymentMethodType }: LoanPaymentMethodProps) => {
  const { paymentMethodDetailEntities } = useCheckoutSessionContext();

  const { refreshPaymentMethodEntity } = useLoanPaymentIntent();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [merchantPosOpened, setMerchantPosOpened] = useState(false);

  const openMerchantPos = useCallback(() => setMerchantPosOpened(true), []);

  const LoanNextActionFactory = useMemo(
    () => (props: Omit<NextActionFactoryProps, 'onOpenMerchantPos'>) =>
      <NextActionFactory {...props} onOpenMerchantPos={openMerchantPos} />,
    [openMerchantPos],
  );

  const paymentMethodDetailEntity = useMemo(
    () =>
      paymentMethodDetailEntities.find((pme) => pme.type === paymentMethodType) as
        | LoanPaymentMethodDetailEntity
        | undefined,
    [paymentMethodDetailEntities, paymentMethodType],
  );

  useEffect(() => {
    if (!paymentMethodDetailEntity) return;
    setLoading(false);
  }, [paymentMethodDetailEntity]);

  useEffect(() => {
    const refresh = async () => {
      if (
        paymentMethodDetailEntity &&
        paymentMethodDetailEntity.isSelected === false &&
        paymentMethodDetailEntity.isRefreshed === false &&
        paymentMethodDetailEntities.length > 0
      ) {
        try {
          await refreshPaymentMethodEntity(paymentMethodDetailEntity);
        } catch (error) {
          setError(error as Error);
        }
      }
    };

    refresh();
  }, [paymentMethodDetailEntity]);

  const hasNoConditionsAvailable =
    (error && error instanceof NoConditionsAvailableException) ||
    (paymentMethodDetailEntity?.error && paymentMethodDetailEntity?.error instanceof NoConditionsAvailableException);

  const loanPaymentMethodDetailEntity =
    paymentMethodDetailEntity instanceof LoanPaymentMethodDetailEntity ? paymentMethodDetailEntity : undefined;

  const showSkeletons = !loanPaymentMethodDetailEntity || !loanPaymentMethodDetailEntity.isRefreshed || loading;

  if (hasNoConditionsAvailable) {
    return (
      <>
        <NoConditionsAvailable />
      </>
    );
  }

  return (
    <Box display={'flex'} flexDirection={'column'} gridGap={24}>
      {showSkeletons ? (
        <>
          <CustomSkeleton variant="rect" width="100%" height="268px" />
          <CustomSkeleton variant="rect" width="100%" height="295px" />
        </>
      ) : (
        <>
          <ConditionsAndProgress
            paymentMethodDetailEntity={loanPaymentMethodDetailEntity}
            onOpenMerchantPos={openMerchantPos}
          />
          <NextActions
            paymentMethodDetailEntity={loanPaymentMethodDetailEntity}
            NextActionFactory={LoanNextActionFactory}
          />
          <PaymentIntentList paymentMethodDetailEntity={loanPaymentMethodDetailEntity} />
        </>
      )}
      {loanPaymentMethodDetailEntity ? (
        <MerchantPosDialog
          opened={merchantPosOpened}
          setOpened={setMerchantPosOpened}
          paymentMethodDetailEntity={loanPaymentMethodDetailEntity}
        />
      ) : null}
    </Box>
  );
};
